import { useCallback, useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { formatDate, formatRp } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PERIODS = [["daily", "Harian"], ["weekly", "Mingguan"], ["monthly", "Bulanan"]];

const toRows = (r) => r.rows.map((x) => ({ "No. Pesanan": x.order_id, "Tanggal Bayar": formatDate(x.paid_at), Pelanggan: x.customer, Status: x.status,
  "Pendapatan Produk": x.revenue, Ongkir: x.shipping, Pajak: x.tax || 0, "Pajak Ditanggung": x.tax_by || "-", Total: x.total, "HPP": x.hpp, "Laba Bersih": x.profit }));

const download = (blob, name) => {
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href);
};

const exportCsv = (r) => {
  const ws = XLSX.utils.json_to_sheet(toRows(r));
  download(new Blob([XLSX.utils.sheet_to_csv(ws)], { type: "text/csv;charset=utf-8" }), `laporan-ellmom-${r.start}_${r.end}.csv`);
};

const exportXlsx = (r) => {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
    { Keterangan: "Periode", Nilai: `${r.start} s.d. ${r.end}` }, { Keterangan: "Jumlah Pesanan", Nilai: r.totals.orders },
    { Keterangan: "Pendapatan Produk", Nilai: r.totals.revenue }, { Keterangan: "Ongkir Diterima", Nilai: r.totals.shipping },
    { Keterangan: "Modal / HPP", Nilai: r.totals.hpp }, { Keterangan: "Pajak E-commerce", Nilai: r.totals.tax }, { Keterangan: "Laba Bersih", Nilai: r.totals.profit }]), "Ringkasan");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(r.series.map((b) => ({ Periode: b.label, Pesanan: b.orders, Pendapatan: b.revenue, HPP: b.hpp, Laba: b.profit }))), "Grafik");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(toRows(r)), "Transaksi");
  XLSX.writeFile(wb, `laporan-ellmom-${r.start}_${r.end}.xlsx`);
};

const exportPdf = (r) => {
  const doc = new jsPDF({ orientation: "landscape" });
  doc.setFontSize(14); doc.text("Laporan Keuangan EllMom Handmade", 14, 16);
  doc.setFontSize(10); doc.text(`Periode ${r.start} s.d. ${r.end}`, 14, 23);
  doc.text(`Pesanan: ${r.totals.orders}   Pendapatan: ${formatRp(r.totals.revenue)}   Ongkir: ${formatRp(r.totals.shipping)}   HPP: ${formatRp(r.totals.hpp)}   Pajak: ${formatRp(r.totals.tax)}   Laba Bersih: ${formatRp(r.totals.profit)}`, 14, 30);
  autoTable(doc, { startY: 36, head: [["No. Pesanan", "Tanggal Bayar", "Pelanggan", "Status", "Pendapatan", "Ongkir", "HPP", "Pajak", "Laba"]],
    body: r.rows.map((x) => [x.order_id, formatDate(x.paid_at), x.customer, x.status, formatRp(x.revenue), formatRp(x.shipping), formatRp(x.hpp), formatRp(x.tax || 0), formatRp(x.profit)]),
    styles: { fontSize: 8 }, headStyles: { fillColor: [180, 83, 9] } });
  doc.save(`laporan-ellmom-${r.start}_${r.end}.pdf`);
};

const Totals = ({ t }) => (
  <div className="grid grid-cols-2 divide-x divide-y divide-stone-200 rounded-lg border border-stone-200 bg-white md:grid-cols-6 md:divide-y-0">
    {[["Pesanan lunas", t.orders, "orders"], ["Pendapatan produk", formatRp(t.revenue), "revenue"], ["Ongkir diterima", formatRp(t.shipping), "shipping"], ["Modal / HPP", formatRp(t.hpp), "hpp"], ["Pajak e-commerce", formatRp(t.tax || 0), "tax"], ["Laba bersih", formatRp(t.profit), "profit"]].map(([l, v, k]) => (
      <div key={k} className="p-4"><p className="text-xs text-stone-500">{l}</p><p className={`tnum mt-1 text-xl font-semibold ${k === "profit" ? "text-green-800" : ""}`} data-testid={`report-total-${k}`}>{v}</p></div>
    ))}
  </div>
);

export default function AdminReports() {
  const call = useApi();
  const [period, setPeriod] = useState("daily");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [r, setR] = useState(null);

  const load = useCallback(() => call("get", "/admin/report", undefined, { params: { period, start: start || undefined, end: end || undefined } }).then(setR), [call, period, start, end]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <PageHeader title="Laporan Keuangan">
        <Button variant="outline" size="sm" disabled={!r} onClick={() => exportCsv(r)} data-testid="export-csv-button"><Download className="mr-1 h-4 w-4" />CSV</Button>
        <Button variant="outline" size="sm" disabled={!r} onClick={() => exportXlsx(r)} data-testid="export-excel-button"><Download className="mr-1 h-4 w-4" />Excel</Button>
        <Button size="sm" disabled={!r} onClick={() => exportPdf(r)} data-testid="export-pdf-button"><Download className="mr-1 h-4 w-4" />PDF</Button>
      </PageHeader>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex rounded-lg border border-stone-200 bg-white p-1">
          {PERIODS.map(([k, l]) => (
            <button key={k} data-testid={`report-period-${k}`} onClick={() => setPeriod(k)} className={`rounded-md px-3 py-1.5 text-sm ${period === k ? "bg-amber-700 text-white" : "text-stone-600 hover:bg-stone-100"}`}>{l}</button>
          ))}
        </div>
        <Input type="date" className="w-40 bg-white" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start-date" />
        <span className="pb-2 text-sm text-stone-500">s.d.</span>
        <Input type="date" className="w-40 bg-white" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end-date" />
      </div>
      {r && <>
        <p className="text-xs text-stone-500">Periode {r.start} s.d. {r.end}. Laba bersih = pendapatan produk − HPP − pajak yang ditanggung toko (ongkir dicatat terpisah).</p>
        <Totals t={r.totals} />
        <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="report-chart">
          <p className="mb-4 font-semibold">Grafik penjualan</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={r.series}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}rb` : v)} width={48} />
                <Tooltip formatter={(v, n) => [formatRp(v), n]} />
                <Bar dataKey="revenue" name="Pendapatan" fill="#b45309" radius={[4, 4, 0, 0]} />
                <Bar dataKey="profit" name="Laba" fill="#0f766e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-stone-100 text-left text-xs text-stone-600"><tr><th className="p-3">No. Pesanan</th><th className="p-3">Tanggal Bayar</th><th className="p-3">Pelanggan</th><th className="p-3 text-right">Pendapatan</th><th className="p-3 text-right">Ongkir</th><th className="p-3 text-right">HPP</th><th className="p-3 text-right">Pajak</th><th className="p-3 text-right">Laba</th></tr></thead>
            <tbody className="divide-y divide-stone-100">
              {r.rows.map((x) => (
                <tr key={x.order_id}><td className="p-3 font-medium">{x.order_id}</td><td className="p-3 text-stone-600">{formatDate(x.paid_at)}</td><td className="p-3">{x.customer}</td>
                  <td className="tnum p-3 text-right">{formatRp(x.revenue)}</td><td className="tnum p-3 text-right">{formatRp(x.shipping)}</td><td className="tnum p-3 text-right">{formatRp(x.hpp)}</td><td className="tnum p-3 text-right">{formatRp(x.tax || 0)}</td><td className="tnum p-3 text-right text-green-800">{formatRp(x.profit)}</td></tr>
              ))}
            </tbody>
          </table>
          {r.rows.length === 0 && <p className="p-6 text-center text-sm text-stone-500" data-testid="report-empty">Belum ada transaksi lunas pada periode ini.</p>}
        </div>
      </>}
    </div>
  );
}
